import { X, Zap, Loader2, CheckCircle2, XCircle, FlaskConical, Info, Pencil, RotateCw } from "lucide-react";

// Metadados do teste por plataforma: onde o usuário acha o código e confere o evento.
const PLATFORM_TEST = {
    facebook: {
        label: "Facebook Pixel",
        manager: "Gerenciador de Eventos da Meta",
        steps: [
            "Abra o Gerenciador de Eventos da Meta e selecione o seu pixel.",
            "Entre na aba \"Eventos de teste\".",
            "Copie o código exibido (começa com TEST, ex: TEST12345).",
            "Cole no campo \"Test Event Code\" deste perfil e salve.",
        ],
    },
    tiktok: {
        label: "TikTok Pixel",
        manager: "Gerenciador de Eventos do TikTok",
        steps: [
            "Abra o Gerenciador de Eventos do TikTok e selecione o seu pixel.",
            "Entre na aba \"Testar eventos\".",
            "Copie o código de teste exibido (ex: TEST12345).",
            "Cole no campo \"Test Event Code\" deste perfil e salve.",
        ],
    },
};

const platformOf = (profile) => {
    if (profile?.platform === "tiktok" || profile?.platform === "facebook") return profile.platform;
    if (!profile?.facebook?.pixel_id && profile?.tiktok?.pixel_id) return "tiktok";
    return "facebook";
};

// Decide o que o teste precisa antes de chamar a API.
// "no_pixel" → perfil sem pixel (ex: só UTMify); "missing_code" → falta o Test Event Code.
export function precheckPixelTest(profile) {
    const platform = platformOf(profile);
    const cfg = profile?.[platform] || {};
    if (!cfg.pixel_id) return { status: "no_pixel", platform };
    if (!cfg.test_event_code) return { status: "missing_code", platform };
    return { status: "ready", platform };
}

function Body({ icon, tone, title, children }) {
    const tones = {
        brand: "bg-[rgba(139,92,246,0.12)] border-[rgba(139,92,246,0.18)] text-[#a78bfa]",
        green: "bg-emerald-500/10 border-emerald-500/20 text-emerald-400",
        red: "bg-red-500/10 border-red-500/20 text-red-400",
        amber: "bg-amber-500/10 border-amber-500/20 text-amber-400",
    };
    return (
        <div className="flex items-start gap-3">
            <div className={`flex-shrink-0 w-10 h-10 rounded-xl border flex items-center justify-center ${tones[tone]}`}>
                {icon}
            </div>
            <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-white">{title}</h3>
                <div className="mt-1 text-sm text-white/60 space-y-3">{children}</div>
            </div>
        </div>
    );
}

function Reassurance() {
    return (
        <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] px-3 py-2.5 flex items-start gap-2 text-xs text-white/55">
            <Info size={14} className="text-white/40 flex-shrink-0 mt-[1px]" />
            <span>
                O Test Event Code é <strong className="text-white/75">opcional</strong> e serve apenas para este teste.
                Sem ele, seu pixel continua enviando as vendas reais normalmente.
            </span>
        </div>
    );
}

/**
 * state: { profile, status: "missing_code" | "no_pixel" | "sending" | "success" | "error", platform, message? }
 */
export default function PixelTestModal({ state, onClose, onEdit, onRetry }) {
    if (!state) return null;
    const { profile, status, platform, message } = state;
    const meta = PLATFORM_TEST[platform] || PLATFORM_TEST.facebook;
    const busy = status === "sending";

    let content;
    let actions;

    if (status === "missing_code") {
        content = (
            <Body icon={<FlaskConical size={20} />} tone="amber" title="Adicione o Test Event Code para testar">
                <p>
                    O botão <strong className="text-white/80">Testar</strong> envia uma compra fictícia para a aba de testes
                    do {meta.manager}, e para isso precisa do <strong className="text-white/80">Test Event Code</strong>.
                    Este perfil ainda não tem um código cadastrado.
                </p>
                <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-white/40 mb-2">Como obter o código</div>
                    <ol className="space-y-1.5">
                        {meta.steps.map((s, i) => (
                            <li key={i} className="flex gap-2">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-white/[0.06] text-[11px] font-semibold text-white/70 flex items-center justify-center">{i + 1}</span>
                                <span>{s}</span>
                            </li>
                        ))}
                    </ol>
                </div>
                <Reassurance />
            </Body>
        );
        actions = (
            <>
                <button type="button" onClick={onClose} className="btn-secondary">Agora não</button>
                <button type="button" onClick={() => onEdit(profile)} className="btn-primary flex items-center gap-2">
                    <Pencil size={15} /> Adicionar código
                </button>
            </>
        );
    } else if (status === "no_pixel") {
        content = (
            <Body icon={<Info size={20} />} tone="brand" title="Nada para testar neste perfil">
                <p>
                    O teste de evento está disponível apenas para perfis com <strong className="text-white/80">Facebook Pixel</strong> ou{" "}
                    <strong className="text-white/80">TikTok Pixel</strong> configurado. Este perfil não tem Pixel ID cadastrado.
                </p>
            </Body>
        );
        actions = <button type="button" onClick={onClose} className="btn-primary">Entendi</button>;
    } else if (status === "sending") {
        content = (
            <Body icon={<Loader2 size={20} className="animate-spin" />} tone="brand" title="Enviando evento de teste...">
                <p>Enviando uma compra fictícia (Purchase) para o {meta.label}.</p>
            </Body>
        );
        actions = <button type="button" disabled className="btn-secondary" style={{ opacity: 0.5 }}>Aguarde</button>;
    } else if (status === "success") {
        content = (
            <Body icon={<CheckCircle2 size={20} />} tone="green" title="Evento de teste enviado">
                <p>
                    O {meta.label} aceitou o evento <strong className="text-white/80">Purchase</strong> de teste.
                    Confira na aba de testes do {meta.manager} — pode levar até 1 minuto para aparecer.
                </p>
                <p className="text-xs text-white/45">
                    Se não aparecer, verifique se o Test Event Code cadastrado é o mesmo exibido agora no {meta.manager}.
                </p>
            </Body>
        );
        actions = <button type="button" onClick={onClose} className="btn-primary">Concluir</button>;
    } else {
        content = (
            <Body icon={<XCircle size={20} />} tone="red" title={`O ${meta.label} recusou o evento de teste`}>
                {message && <p className="text-white/75">{message}</p>}
                <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-white/40 mb-2">O que conferir</div>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>O <strong className="text-white/80">Pixel ID</strong> está correto e pertence à sua conta.</li>
                        <li>O <strong className="text-white/80">Access Token</strong> é válido e não expirou.</li>
                        <li>O <strong className="text-white/80">Test Event Code</strong> é o atual exibido no {meta.manager}.</li>
                    </ul>
                </div>
            </Body>
        );
        actions = (
            <>
                <button type="button" onClick={() => onEdit(profile)} className="btn-secondary flex items-center gap-2">
                    <Pencil size={15} /> Editar perfil
                </button>
                <button type="button" onClick={() => onRetry(profile)} className="btn-primary flex items-center gap-2">
                    <RotateCw size={15} /> Tentar novamente
                </button>
            </>
        );
    }

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-2 md:p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={busy ? undefined : onClose} />
            <div
                role="dialog"
                aria-modal="true"
                className="relative w-full max-w-lg rounded-2xl border border-white/[0.09] bg-[#10141a] p-4 md:p-6 shadow-[0_24px_64px_rgba(0,0,0,0.55)] text-white"
                style={{ maxHeight: "92vh", overflowY: "auto", colorScheme: "dark" }}
            >
                <div className="flex items-center justify-between mb-5 gap-3">
                    <div className="flex items-center gap-2 min-w-0 text-sm text-white/50">
                        <Zap size={14} className="flex-shrink-0" />
                        <span className="truncate">Testar pixel · <span className="text-white/75">{profile?.name}</span></span>
                    </div>
                    <button onClick={onClose} disabled={busy} className="action-btn flex-shrink-0" aria-label="Fechar">
                        <X size={18} />
                    </button>
                </div>

                {content}

                <div className="flex gap-3 justify-end mt-6">{actions}</div>
            </div>
        </div>
    );
}
